import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { LottieRefCurrentProps } from 'lottie-react';

import useResponsive from '@/shared/hooks/useResponsive';

import { AlienHandle } from '@/features/timeGame/components/elements/Alien';

type Options = {
	duration?: number;
	questionIndex?: number;
	onAnswerBlockAnimationStart?: () => void;
};

const ALIEN_VERTICAL_OFFSET_FACTOR = 0.15; // Factor to adjust alien's vertical position when entering warp tube
const ALIEN_HORIZONTAL_OFFSET_FACTOR = 0.08; // Factor to adjust alien's horizontal position when entering warp tube
const HOP_HEIGHT_MULTIPLIER = 0.7; // Multiplier to determine hop height based on distance

export const useQuestionAnimation = ({ duration = 0.6, onAnswerBlockAnimationStart }: Options = {}) => {
	const { isPortrait } = useResponsive();
	const aliensContainerRef = useRef<HTMLDivElement | null>(null);
	const questionBlockRef = useRef<HTMLDivElement | null>(null);
	const answerBlockRef = useRef<HTMLDivElement | null>(null);
	const timelineRef = useRef<gsap.core.Timeline | null>(null);
	const conveyorAnimationRef = useRef<LottieRefCurrentProps | null>(null);

	const setConveyorAnimationRef = useCallback((ref: LottieRefCurrentProps | null) => {
		conveyorAnimationRef.current = ref;
	}, []);

	const playConveyor = useCallback(() => {
		const conveyor = conveyorAnimationRef.current;
		if (!conveyor) return;
		conveyor.stop();
		conveyor.play();
	}, []);

	// Set initial positions
	useLayoutEffect(() => {
		// Aliens container stays in place, fully visible
		if (aliensContainerRef.current) {
			gsap.set(aliensContainerRef.current, { x: '0%', opacity: 1 });
		}

		// Position question block - from left in portrait, from top in landscape
		if (questionBlockRef.current) {
			if (isPortrait) {
				gsap.set(questionBlockRef.current, { x: '-200%', opacity: 0 });
			} else {
				gsap.set(questionBlockRef.current, { y: '-200%', opacity: 0 });
			}
		}

		// Position answer block - from left in portrait, from top in landscape
		if (answerBlockRef.current) {
			if (isPortrait) {
				gsap.set(answerBlockRef.current, { x: '-200%', opacity: 1 });
			} else {
				gsap.set(answerBlockRef.current, { y: '-300%', opacity: 1 });
			}
		}
	}, [isPortrait]);

	// Cleanup timeline on unmount
	useEffect(() => {
		return () => {
			timelineRef.current?.kill();
		};
	}, []);

	const playQuestionAnimation = useCallback(
		(currentQuestionIndex: number = 0): Promise<void> => {
			return new Promise((resolve) => {
				timelineRef.current?.kill();

				if (!aliensContainerRef.current || !questionBlockRef.current || !answerBlockRef.current) {
					resolve();
					return;
				}

				// Find all aliens (not just visible ones)
				const aliens = aliensContainerRef.current.querySelectorAll('.conveyor-belt__alien');
				const warpTubeBottom = document.querySelector('.warp-tube__bottom');
				const warpTubeGlassSvg = document.querySelector('.warp-tube__glass svg');

				const timeline = gsap.timeline({
					onComplete: () => {
						resolve();
					},
				});

				// Step 1: Move the alien corresponding to the current question index to warp tube
				if (aliens.length > 0 && aliens[currentQuestionIndex] && warpTubeBottom) {
					const targetAlien = aliens[currentQuestionIndex] as HTMLElement;
					const bottomRect = warpTubeBottom.getBoundingClientRect();
					const alienRect = targetAlien.getBoundingClientRect();

					// Calculate position to center horizontally (offset 10% left) and align bottom to top of warp-tube__bottom
					const targetX =
						bottomRect.left +
						bottomRect.width / 2 -
						(alienRect.left + alienRect.width / 2) -
						bottomRect.width * ALIEN_HORIZONTAL_OFFSET_FACTOR;
					// Position alien's bottom at the top of warp-tube__bottom
					const targetBottomPosition = bottomRect.top;
					const targetY =
						targetBottomPosition -
						(alienRect.top + alienRect.height) +
						alienRect.height * ALIEN_VERTICAL_OFFSET_FACTOR;

					// Add alien movement animation with a little hop into the tube
					const hopHeight = Math.max(
						100,
						Math.min(200, Math.hypot(targetX, targetY) * HOP_HEIGHT_MULTIPLIER),
					);
					timeline.to(targetAlien, {
						keyframes: [
							{
								x: targetX,
								y: targetY - hopHeight,
								ease: 'power1.out',
								duration: duration * 0.5,
							},
							{
								x: targetX,
								y: targetY,
								ease: 'power1.in',
								duration: duration * 0.5,
							},
						],
					});

					// Step 2: Warp tube glass slides down to close (with small delay to ensure alien is inside)
					timeline.to(
						warpTubeGlassSvg,
						{
							y: 0,
							duration: duration,
							ease: 'power2.out',
						},
						'+=0.5',
					);
				}

				// Step 3: Question block slides in - from left in portrait, from top in landscape
				timeline.to(questionBlockRef.current, {
					...(isPortrait ? { x: '0%' } : { y: '-5%' }),
					opacity: 1,
					ease: 'power2.out',
					duration: duration,
				});

				// Trigger callback before answer block animation starts
				if (onAnswerBlockAnimationStart) {
					timeline.call(onAnswerBlockAnimationStart);
				}

				// Step 4: Answer block slides in - from left in portrait, from top in landscape
				timeline.to(answerBlockRef.current, {
					...(isPortrait ? { x: '0%' } : { y: '0%' }),
					ease: 'power2.out',
					duration: duration * 1.3,
				});

				timelineRef.current = timeline;
			});
		},
		[duration, onAnswerBlockAnimationStart, isPortrait],
	);

	const playAnswerAnimation = useCallback(
		(isCorrect: boolean, targetAlienHandle?: AlienHandle): Promise<void> => {
			return new Promise((resolve) => {
				const warpTubeGlass = document.querySelector('.warp-tube__glass');
				const warpTubeGlassSvg = document.querySelector('.warp-tube__glass svg');
				const warpTubeBottom = document.querySelector('.warp-tube__bottom');

				if (!warpTubeGlass || !warpTubeBottom) {
					resolve();
					return;
				}

				const glassColorClass = isCorrect ? 'warp-tube__glass--green' : 'warp-tube__glass--red';
				const bottomColorClass = isCorrect ? 'warp-tube__bottom--green' : 'warp-tube__bottom--red';
				const blinkDuration = duration * 0.3;

				const targetAlien = targetAlienHandle?.element;

				const timeline = gsap.timeline();

				// Blink between default and answer color 3 times
				for (let i = 0; i < 3; i++) {
					timeline
						.call(() => {
							// Glass color
							warpTubeGlass.classList.remove('warp-tube__glass--default');
							warpTubeGlass.classList.add(glassColorClass);
							// Bottom color
							warpTubeBottom.classList.remove('warp-tube__bottom--default');
							warpTubeBottom.classList.add(bottomColorClass);
						})
						.to({}, { duration: blinkDuration })
						.call(() => {
							// Glass color
							warpTubeGlass.classList.remove(glassColorClass);
							warpTubeGlass.classList.add('warp-tube__glass--default');
							// Bottom color
							warpTubeBottom.classList.remove(bottomColorClass);
							warpTubeBottom.classList.add('warp-tube__bottom--default');
						})
						.to({}, { duration: blinkDuration });
				}

				if (targetAlien) {
					if (isCorrect) {
						timeline
							.to(targetAlien, {
								y: '-75%',
								duration: duration,
								ease: 'power2.in',
							})
							.call(
								() =>
									targetAlienHandle?.playAnswerAnimation?.(isCorrect, () => {
										resolve();
									}),
								[],
								'<',
							);
					} else {
						timeline
							.to(warpTubeGlassSvg, {
								y: '-90%',
								duration: duration,
								ease: 'power2.in',
							})
							// Move alien down slightly to align with wrong answer animation position
							.call(() => {
								targetAlienHandle?.playAnswerAnimation?.(isCorrect);
							})
							.to(
								targetAlien,
								{
									x: '-200%',
									duration: duration,
									ease: 'power2.in',
									onComplete: () => {
										resolve();
									},
								},
								'+=1',
							);
					}
				} else {
					timeline.eventCallback('onComplete', () => {
						resolve();
					});
				}

				timeline.play();
			});
		},
		[duration],
	);

	const playExitAnimation = useCallback((): Promise<void> => {
		return new Promise((resolve) => {
			const warpTubeGlassSvg = document.querySelector('.warp-tube__glass svg');

			if (!questionBlockRef.current || !answerBlockRef.current || !warpTubeGlassSvg) {
				resolve();
				return;
			}

			const exitTimeline = gsap.timeline({
				onComplete: () => {
					resolve();
				},
			});

			// Move all elements - question and answer blocks exit left in portrait, up in landscape
			exitTimeline
				.to(
					warpTubeGlassSvg,
					{
						y: '-90%',
						duration: duration,
						ease: 'power2.in',
					},
					0,
				)
				.to(
					questionBlockRef.current,
					{
						...(isPortrait ? { x: '-200%' } : { y: '-200%' }),
						opacity: 0,
						duration: duration,
						ease: 'power2.in',
					},
					0,
				)
				.to(
					answerBlockRef.current,
					{
						...(isPortrait ? { x: '-200%' } : { y: '-300%' }),
						duration: duration,
						ease: 'power2.in',
					},
					0,
				);

			// After exit animations, move remaining aliens one position to the right
			if (aliensContainerRef.current) {
				const allAliens = aliensContainerRef.current.querySelectorAll('.conveyor-belt__alien');
				const remainingAliens: HTMLElement[] = [];

				// Find all aliens that haven't been answered yet (don't have the --answered class)
				allAliens.forEach((alien) => {
					if (!alien.classList.contains('conveyor-belt__alien--answered')) {
						remainingAliens.push(alien as HTMLElement);
					}
				});

				// Animate each remaining alien to move right by 40% (one position)
				if (remainingAliens.length > 0) {
					const moveStartPosition = '<';
					exitTimeline.call(playConveyor, [], moveStartPosition);
					remainingAliens.forEach((alien) => {
						exitTimeline.to(
							alien,
							{
								left: '+=40%',
								duration: duration * 0.2,
								ease: 'linear',
							},
							'<',
						);
					});
				}
			}
		});
	}, [duration, playConveyor, isPortrait]);

	const resetAnimation = useCallback(() => {
		timelineRef.current?.kill();

		// Reset all elements to initial positions
		if (aliensContainerRef.current) {
			gsap.set(aliensContainerRef.current, { x: '0%', opacity: 1 });
		}

		if (questionBlockRef.current) {
			if (isPortrait) {
				gsap.set(questionBlockRef.current, { x: '-200%', opacity: 0 });
			} else {
				gsap.set(questionBlockRef.current, { y: '-100vh', opacity: 0 });
			}
		}

		if (answerBlockRef.current) {
			if (isPortrait) {
				gsap.set(answerBlockRef.current, { x: '-200%', opacity: 1 });
			} else {
				gsap.set(answerBlockRef.current, { y: '-120vh', opacity: 1 });
			}
		}
	}, [isPortrait]);

	return {
		aliensContainerRef,
		questionBlockRef,
		answerBlockRef,
		setConveyorAnimationRef,
		playQuestionAnimation,
		playAnswerAnimation,
		playExitAnimation,
		resetAnimation,
	};
};

export type UseQuestionAnimationReturn = ReturnType<typeof useQuestionAnimation>;

export default useQuestionAnimation;
